import {
  GreyGrey20,
  Primary12Regular,
  GreyGrey70,
  RedRed60,
  GreyGrey60,
  Primary14SemiBold,
  YellowYellow10,
  YellowYellow80,
  SequenceMenu,
  Button,
  IconEllipsisHorizontal,
  IconCheckCircle,
  IconExclamationTriangle,
  IconChevronDown,
  IconChevronRight,
  IconTag,
  Tooltip,
  Badge,
  Center,
  Box,
  Flex,
  Grid,
  SequenceMenuItemType,
  IconEyeSlash
} from '@sequencehq/core-components'
import { match } from 'ts-pattern'
import { FC, useState } from 'react'
import { usePhaseDetails } from './usePhaseDetails'
import { usePhaseActions } from './usePhaseActions'
import { PhaseTitle } from './PhaseTitle'
import { usePhaseName } from './usePhaseName'
import { usePhaseContractTag } from './usePhaseContractTag'
import { ContractTagModal } from './contractTag.modal'
import { usePhasePermissions } from 'Cube/utils/usePhasePermissions'
import { usePhaseAlignmentWarning } from './PhaseAlignmentWarning'
import { useOpenEndedPhaseHidden } from './useOpenEndedPhaseHidden'
import { ContractReferenceType } from 'Cube/domain/cube.domain.types'
import { ContractReferenceBadges } from './contractReferences/ContractReferenceBadges'
import { ContractReferenceModal } from './contractReferences/contractReference.modal'
import { useContractReferenceMenuItems } from './contractReferences/useContractReferenceMenuItems'

// Config items only, so we can tell whether any of them is enabled.
type PhaseMenuItem = Extract<SequenceMenuItemType, { label: string }>

export const PhaseHeader: FC<{
  phaseId: string
  onExpand: (newState: boolean) => void
  expanded: boolean
  onInsertBefore?: () => void
  onInsertAfter?: () => void
}> = props => {
  const { visible: phaseAlignmentWarningVisible } = usePhaseAlignmentWarning({
    phaseId: props.phaseId
  })
  const phaseDetails = usePhaseDetails({ phaseId: props.phaseId })
  const openEndedPhaseHidden = useOpenEndedPhaseHidden({
    phaseId: props.phaseId
  })
  const phaseActions = usePhaseActions({ phaseId: props.phaseId })
  const phaseName = usePhaseName({ phaseId: props.phaseId })
  const contractTag = usePhaseContractTag({ phaseId: props.phaseId })
  const [isEditingName, setIsEditingName] = useState(false)
  const [isEditingContractTag, setIsEditingContractTag] = useState(false)
  const [addingReferenceType, setAddingReferenceType] =
    useState<ContractReferenceType | null>(null)
  const contractReferenceMenuItems = useContractReferenceMenuItems({
    phaseId: props.phaseId,
    onAdd: setAddingReferenceType
  })

  const { canUpdatePhase } = usePhasePermissions()

  const ChevronIcon = props.expanded ? IconChevronDown : IconChevronRight

  const contractTagDisabled = !contractTag.available || !canUpdatePhase

  const contractTagMenuItems: PhaseMenuItem[] = match({
    visible: contractTag.visible,
    hasTag: Boolean(contractTag.contractTag)
  })
    .with({ visible: true, hasTag: true }, () => [
      {
        label: 'Edit contract tag',
        onClick: () => setIsEditingContractTag(true),
        isDisabled: contractTagDisabled,
        'data-testid': 'phase.contractTag.edit'
      },
      {
        label: 'Delete contract tag',
        onClick: contractTag.deleteContractTag,
        isDisabled: contractTagDisabled,
        divider: true,
        style: { color: RedRed60 },
        'data-testid': 'phase.contractTag.delete'
      }
    ])
    .with({ visible: true, hasTag: false }, () => [
      {
        label: 'Add contract tag',
        onClick: () => setIsEditingContractTag(true),
        isDisabled: contractTagDisabled,
        divider: true,
        'data-testid': 'phase.contractTag.add'
      }
    ])
    .otherwise(() => [])

  const menuItems: PhaseMenuItem[] = [
    {
      label: phaseName.name ? 'Edit name' : 'Add name',
      onClick: () => setIsEditingName(true),
      isDisabled: !phaseName.available || isEditingName || !canUpdatePhase,
      divider: !phaseName.name,
      'data-testid': 'phase.name.edit'
    },
    ...(phaseName.name
      ? [
          {
            label: 'Delete name',
            onClick: () => {
              setIsEditingName(false)
              phaseName.updateName('')
            },
            isDisabled: !phaseName.available,
            divider: true,
            'data-testid': 'phase.name.delete'
          }
        ]
      : []),
    ...contractTagMenuItems,
    ...contractReferenceMenuItems,
    ...(!canUpdatePhase || phaseDetails.insertBeforeDisabled
      ? []
      : [
          {
            label: 'Add phase before',
            onClick: () => props.onInsertBefore?.(),
            'data-testid': 'phase.menu.insertBefore'
          }
        ]),
    ...(!canUpdatePhase || phaseDetails.insertAfterDisabled
      ? []
      : [
          {
            label: 'Add phase after',
            onClick: () => props.onInsertAfter?.(),
            divider: true,
            'data-testid': 'phase.menu.insertAfter'
          }
        ]),
    {
      label: 'Delete phase',
      onClick: phaseActions.deletePhase.handler,
      style: { color: RedRed60 },
      isDisabled: !phaseActions.deletePhase.available || !canUpdatePhase,
      'data-testid': 'phase.menu.deletePhase'
    }
  ]

  const hasEnabledMenuItem = menuItems.some(item => !item.isDisabled)

  return (
    <Grid
      // Grows past 48px only when the reference pills wrap onto more rows.
      minHeight="48px"
      templateColumns={'1fr auto'}
      columnGap="8px"
      padding="0 8px 0 0"
      alignItems="center"
      {...(phaseDetails.isOnlyPhase
        ? {}
        : {
            cursor: 'pointer',
            onClick: () => props.onExpand(!props.expanded)
          })}
      {...Primary14SemiBold}
      userSelect="none"
      data-testid="phase.header"
    >
      <Flex alignItems="center" gap="8px">
        {!phaseDetails.isOnlyPhase && <ChevronIcon style={{ flexShrink: 0 }} />}
        {/* The name input fills the header, so only hug the title when not editing. */}
        <Flex
          alignItems="center"
          gap="8px"
          width={isEditingName ? '100%' : 'fit-content'}
        >
          <PhaseTitle
            phaseId={props.phaseId}
            isEditing={isEditingName}
            onEditStart={() => setIsEditingName(true)}
            onEditComplete={() => setIsEditingName(false)}
          />
          {openEndedPhaseHidden && !isEditingName && (
            <Tooltip
              label="This phase is hidden on the published quote"
              placement="top"
            >
              <Center flexShrink="0" data-testid="phase.hiddenFromQuote">
                <IconEyeSlash color={GreyGrey60} />
              </Center>
            </Tooltip>
          )}
        </Flex>
        {phaseAlignmentWarningVisible && (
          <Flex
            backgroundColor={YellowYellow10}
            borderRadius="6px"
            height="24px"
            width="24px"
            alignItems="center"
            justifyContent="center"
          >
            <IconExclamationTriangle color={YellowYellow80} />
          </Flex>
        )}
      </Flex>
      <Flex alignItems="center" gap="8px" minWidth="0">
        <ContractReferenceBadges phaseId={props.phaseId} />
        {contractTag.visible && contractTag.contractTag && (
          <Badge
            sentiment="neutral"
            icon={IconTag}
            data-testid="phase.contractTag.badge"
          >
            {contractTag.contractTag}
          </Badge>
        )}
        <Box>
          {phaseDetails.phaseComplete && (
            <Flex
              backgroundColor={GreyGrey20}
              borderRadius="6px"
              alignItems="center"
              padding="0 6px"
              height="24px"
              color={GreyGrey70}
              gap="4px"
              data-testid="phase.header.completed"
              {...Primary12Regular}
            >
              <IconCheckCircle color={GreyGrey70} />
              Completed
            </Flex>
          )}
        </Box>
        {!isEditingName && (
          <Box onClick={e => e.stopPropagation()}>
            {hasEnabledMenuItem && (
              <SequenceMenu
                alignment="bottom-right"
                data-testid={`phase.${props.phaseId}.menu`}
                menuButton={
                  <Button
                    variant="ghost"
                    size="24px"
                    data-testid="phase.options"
                  >
                    <Center flexShrink="0">
                      <IconEllipsisHorizontal color={GreyGrey60} />
                    </Center>
                  </Button>
                }
                items={menuItems}
              />
            )}
            {addingReferenceType && (
              <ContractReferenceModal
                phaseId={props.phaseId}
                type={addingReferenceType}
                onClose={() => setAddingReferenceType(null)}
              />
            )}
            {isEditingContractTag && (
              <ContractTagModal
                initialValue={contractTag.contractTag}
                onSubmit={contractTag.updateContractTag}
                onClose={() => setIsEditingContractTag(false)}
              />
            )}
          </Box>
        )}
      </Flex>
    </Grid>
  )
}
